import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import { dataService, isDemoMode } from '@/services/data-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';
import type { Location, Category } from '@/types/settings';

// Fallback mock data for dropdowns (used when API fails or in demo mode)
const TRANSACTION_TYPES = ['Purchase', 'Disposal', 'Transfer', 'Depreciation', 'Revaluation', 'Impairment', 'Write-off'];
const STATUSES = ['Completed', 'Pending', 'Failed', 'Cancelled', 'In Progress'];
const FALLBACK_CATEGORIES = ['IT Equipment', 'Furniture', 'Vehicles', 'Machinery', 'Buildings', 'Land'];
const FALLBACK_LOCATIONS = ['Head Office', 'Warehouse A', 'Warehouse B', 'Branch 1', 'Branch 2', 'Remote'];
const CREATED_BY = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown', 'System'];
const FISCAL_PERIODS = ['FY2024-Q1', 'FY2024-Q2', 'FY2024-Q3', 'FY2024-Q4', 'FY2023-Q4', 'FY2023-Q3'];

export const FiltersTab: React.FC = () => {
  const { 
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  // Transaction Type
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  // Transaction Status
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Date Range
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Amount Range
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');

  // Asset Category
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Location
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Created By
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string[]>([]);

  // Fiscal Period
  const [selectedFiscalPeriods, setSelectedFiscalPeriods] = useState<string[]>([]);

  // Save filter modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Selected saved filter
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Loading state for API operations
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // API-fetched filter options
  const [apiLocations, setApiLocations] = useState<Location[]>([]);
  const [apiCategories, setApiCategories] = useState<Category[]>([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  // Ref to track if we're loading from external source
  const isLoadingFromExternal = React.useRef(false);

  // Fetch filter options from API on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilterOptions(true);
      try {
        const [locations, categories] = await Promise.all([
          dataService.getLocations(),
          dataService.getCategories(),
        ]);
        setApiLocations(locations);
        setApiCategories(categories);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setIsLoadingFilterOptions(false);
      }
    };
    fetchFilterOptions();
  }, []);

  // Compute filter options from API data (only use fallback in demo mode)
  const locationOptions = useMemo(() => {
    if (apiLocations.length > 0) {
      return apiLocations.map(loc => ({ label: loc.name, value: loc.name }));
    }
    if (isDemoMode()) {
      return FALLBACK_LOCATIONS.map(loc => ({ label: loc, value: loc }));
    }
    return [];
  }, [apiLocations]);

  const categoryOptions = useMemo(() => {
    if (apiCategories.length > 0) {
      return apiCategories.map(cat => ({ label: cat.code, value: cat.code }));
    }
    if (isDemoMode()) {
      return FALLBACK_CATEGORIES.map(cat => ({ label: cat, value: cat }));
    }
    return [];
  }, [apiCategories]);

  // Load saved filters from API on component mount
  const loadSavedFiltersFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setIsLoading(false);
    }
  }, [setSavedFilters]);

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  // Update pending filters whenever local state changes
  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }
    
    const filters: PendingFilters = {
      selectedCategories,
      selectedStatuses,
      selectedLocations,
      acquisitionDateRange: { from: dateFrom, to: dateTo },
      acquisitionCostMin: amountMin,
      acquisitionCostMax: amountMax,
      netBookValueMin: '',
      netBookValueMax: '',
      selectedAssignedTo: selectedCreatedBy,
      selectedDepreciationMethods: selectedTypes,
      warrantyExpiryDateRange: { from: undefined, to: undefined },
      selectedManufacturers: selectedFiscalPeriods,
    };
    setPendingFilters(filters);
  }, [
    selectedTypes,
    selectedStatuses,
    dateFrom,
    dateTo,
    amountMin,
    amountMax,
    selectedCategories,
    selectedLocations,
    selectedCreatedBy,
    selectedFiscalPeriods,
    setPendingFilters,
  ]);

  // Load pending filters into local state when they change from external source
  useEffect(() => {
    if (pendingFilters) {
      isLoadingFromExternal.current = true;
      setSelectedCategories(pendingFilters.selectedCategories);
      setSelectedStatuses(pendingFilters.selectedStatuses);
      setSelectedLocations(pendingFilters.selectedLocations);
      setDateFrom(pendingFilters.acquisitionDateRange.from);
      setDateTo(pendingFilters.acquisitionDateRange.to);
      setAmountMin(pendingFilters.acquisitionCostMin);
      setAmountMax(pendingFilters.acquisitionCostMax);
      setSelectedCreatedBy(pendingFilters.selectedAssignedTo);
      setSelectedTypes(pendingFilters.selectedDepreciationMethods);
      setSelectedFiscalPeriods(pendingFilters.selectedManufacturers);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters]);

  // Clear local state when filters are cleared
  useEffect(() => {
    if (!pendingFilters && !appliedFilters) {
      isLoadingFromExternal.current = true;
      setSelectedTypes([]);
      setSelectedStatuses([]);
      setDateFrom(undefined);
      setDateTo(undefined);
      setAmountMin('');
      setAmountMax('');
      setSelectedCategories([]);
      setSelectedLocations([]);
      setSelectedCreatedBy([]);
      setSelectedFiscalPeriods([]);
      setSelectedSavedFilterId(null);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters, appliedFilters]);

  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setDateFrom(undefined);
    setDateTo(undefined);
    setAmountMin('');
    setAmountMax('');
    setSelectedCategories([]);
    setSelectedLocations([]);
    setSelectedCreatedBy([]);
    setSelectedFiscalPeriods([]);
    setSelectedSavedFilterId(null);
    clearAllFilters();
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  };

  const activeFilterCount = 
    selectedTypes.length +
    selectedStatuses.length +
    (dateFrom || dateTo ? 1 : 0) +
    (amountMin || amountMax ? 1 : 0) +
    selectedCategories.length +
    selectedLocations.length +
    selectedCreatedBy.length +
    selectedFiscalPeriods.length;

  // Update the context with the active filter count
  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  const hasFilterChanges = () => {
    // If no pending filters exist, no changes can be made
    if (!pendingFilters) return false;
    
    // If no filters are active and nothing is applied, no changes
    if (!appliedFilters && activeFilterCount === 0) return false;
    
    // If we have a selected saved filter, compare pending with the saved filter
    if (selectedSavedFilterId) {
      const selectedFilter = savedFilters.find(f => f.id === selectedSavedFilterId);
      if (selectedFilter) {
        // Compare current pending filters with the saved filter
        return JSON.stringify(pendingFilters) !== JSON.stringify(selectedFilter.filters);
      }
    }
    
    // If there are applied filters but no active filters (user cleared everything), that's a change
    if (appliedFilters && activeFilterCount === 0) return true;
    
    // Otherwise, compare pending with applied filters
    if (!appliedFilters && activeFilterCount > 0) return true;
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
      // Auto-apply the saved filter immediately
      setTimeout(() => {
        applyFilters();
      }, 0);
    } else {
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown */}
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">

      {/* Transaction Type Filter */}
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Transaction Type</Label>
            {selectedTypes.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedTypes.length}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Type of transaction to filter</p>
        </div>

        <MultiSelect
          options={TRANSACTION_TYPES.map(type => ({ label: type, value: type }))}
          value={selectedTypes}
          onValueChange={setSelectedTypes}
          placeholder="Select transaction types..."
        />
      </div>

      <Separator />

      {/* Transaction Status Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Transaction Status</Label>
          {selectedStatuses.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedStatuses.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={STATUSES.map(status => ({ label: status, value: status }))}
          value={selectedStatuses}
          onValueChange={setSelectedStatuses}
          placeholder="Select statuses..."
        />
      </div>

      <Separator />

      {/* Date Range Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Transaction Date</Label>

        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onFromChange={setDateFrom}
          onToChange={setDateTo}
        />
      </div>

      <Separator />

      {/* Amount Range Filter */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Transaction Amount</Label>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="amount-min" className="text-xs text-muted-foreground">Min</Label>
            <Input
              id="amount-min"
              type="number"
              placeholder="0"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="amount-max" className="text-xs text-muted-foreground">Max</Label>
            <Input
              id="amount-max"
              type="number"
              placeholder="999999"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              className="h-9"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Asset Category Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Asset Category</Label>
          {selectedCategories.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedCategories.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={categoryOptions}
          value={selectedCategories}
          onValueChange={setSelectedCategories}
          placeholder={isLoadingFilterOptions ? "Loading..." : "Select categories..."}
        />
      </div>

      <Separator />

      {/* Location Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Location</Label>
          {selectedLocations.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedLocations.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={locationOptions}
          value={selectedLocations}
          onValueChange={setSelectedLocations}
          placeholder={isLoadingFilterOptions ? "Loading..." : "Select locations..."}
        />
      </div>

      <Separator />

      {/* Created By Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Created By</Label>
          {selectedCreatedBy.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedCreatedBy.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={CREATED_BY.map(user => ({ label: user, value: user }))}
          value={selectedCreatedBy}
          onValueChange={setSelectedCreatedBy}
          placeholder="Select users..."
        />
      </div>

      <Separator />

      {/* Fiscal Period Filter */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Fiscal Period</Label>
          {selectedFiscalPeriods.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selectedFiscalPeriods.length}
            </Badge>
          )}
        </div>

        <MultiSelect
          options={FISCAL_PERIODS.map(period => ({ label: period, value: period }))}
          value={selectedFiscalPeriods}
          onValueChange={setSelectedFiscalPeriods}
          placeholder="Select fiscal periods..."
        />
      </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={activeFilterCount === 0}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
